<template>
  <div class="m-5">
    <d-table class="mt-10" :data="assignments" :loading="loadingAssignments" detailed paginated backend-pagination :total="total" backend-sorting :default-sort="[pagination.sortField, pagination.sortOrder]" striped detail-key="id" default-sort-direction="desc" :per-page="pagination.pageSize" @page-change="page => pagination.page = page" @sort="onSort">
      <template #action-buttons>
        <h2 class=" text-primary-500 mb-0">
          Manage Assignments
        </h2>
      </template>
      <template #action-buttons-right>
        <d-checkbox v-model="showArchived" :native-value="true" class="mr-5" @input="getAssignmentsPaged">
          Show Archived
        </d-checkbox>
        <filter-dropdown v-model="filter" />

        <d-btn @click="launchAssignmentsModal()">
          + New Assignment
        </d-btn>
      </template>
      <template slot-scope="props">
        <d-table-column field="name" :row="props.row" label="Assignment Name" sortable>
          <span v-if="props.row.archived">[Archived]</span> {{ props.row.name }}
        </d-table-column>
        <d-table-column field="assignmentCategory.name" :row="props.row" label="Category Name" sortable>
          {{ props.row.categoryName }}
        </d-table-column>
        <d-table-column field="ownerFullName" :row="props.row" label="Created By">
          {{ props.row.ownerFullName }}
        </d-table-column>
        <d-table-column field="dueDate" :row="props.row" label="Due Date" sortable>
          {{ props.row.dueDate | formatShortDate }}
        </d-table-column>
        <d-table-column field="reviewerFullName" :row="props.row" label="Reviewer">
            <span v-if="props.row.reviewersAreMentors">
                Mentees' Mentors
            </span>
            <span v-else-if="props.row.reviewersAreConsultants">
                Mentors' Consultants
            </span>
            <span v-else>
                {{ props.row.reviewerFullName }}
            </span>
        </d-table-column>
        <d-table-column v-if="props.row.groupId !== null" field="groupName" :row="props.row" label="Assigned To">
          <span class="underline cursor-pointer text-secondary-400" @click="launchGroupAssigneesSideDrawer(props.row)"> {{ props.row.groupName }} </span>
        </d-table-column>
        <d-table-column v-else field="userFullName" :row="props.row" label="Assigned To">
          <span class="underline cursor-pointer text-secondary-400" @click="launchIndividualAssigneesSideDrawer(props.row)">{{ props.row.userFullName }} </span>
        </d-table-column>
        <d-table-column field="published" :row="props.row" label="Published">
          {{ props.row.published ? 'Yes' : 'No' }}
        </d-table-column>
        <d-table-column field="allUserAssignmentsCompleted" :row="props.row" label="All Completed">
          {{ props.row.allUserAssignmentsCompleted }}
        </d-table-column>
        <d-table-column class="text-right">
          <div class="flex">
            <d-tooltip label="Duplicate assignment">
              <d-icon icon="copy" class="cursor-pointer text-black ml-4" @click.native="launchDuplicateAssignmentModal(props.row)" />
            </d-tooltip>
            <d-tooltip label="Sync assignment data">
              <d-icon :spin="syncingAssignmentId === props.row.id" :icon="syncingAssignmentId === props.row.id ? 'spinner' : 'sync'" class="cursor-pointer text-black ml-4" @click.native="syncAssignment(props.row)" />
            </d-tooltip>
            <d-icon icon="edit" class="cursor-pointer text-green-500 ml-4" @click.native="launchAssignmentsModal(props.row)" />
            <d-tooltip label="Archive assignment">
              <d-icon icon="archive" class="cursor-pointer text-black ml-4" @click.native="archiveAssignment(props.row)" />
            </d-tooltip>
            <d-icon icon="trash" class="cursor-pointer text-red-500 ml-4" @click.native="deleteAssignment(props.row)" />
          </div>
        </d-table-column>
      </template>
      <template slot="detail" slot-scope="props">
        <to-dos-sub-page :assignment="props.row" />
      </template>
    </d-table>
  </div>
</template>

<script>
  import axios from 'axios';
  import AssignmentTemplateModal from './components/AssignmentTemplateModal';
  import IndividualAssigneeSideDrawer from './components/IndividualAssigneeSideDrawer';
  import GroupAssigneesSideDrawer from './components/GroupAssigneesSideDrawer';

  export default {
    parent: true,
    data: () => ({
      assignments: [],
      syncingAssignment: false,
      loadingAssignments: false,
      syncingAssignmentId: 0,
      total: 0,
      filter: '',
      showArchived: false,
      pagination: {
        page: 1,
        pageSize: 50,
        sortField: 'name',
        sortOrder: 'asc',
      },
    }),

    watch: {
      pagination: {
        handler() {
          this.getAssignmentsPaged();
        },
        deep: true,
      },
      filter() {
        this.getAssignmentsPaged();
      },
    },
    mounted() {
      this.getAssignmentsPaged();
    },

    methods: {
      async getAssignmentsPaged() {
        const {
          page, sortField, sortOrder, pageSize,
        } = this.pagination;
        const params = [`filter=${this.filter}`, `sortDir=${sortField} ${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`, `showArchived=${this.showArchived}`].join('&');
        this.loadingAssignments = true;
        const { data } = await axios.get(`/api/admin/assignments/paged?${params}`);
        this.assignments = data.resultItems;
        this.total = data.totalRecords;
        this.loadingAssignments = false;
      },

      async syncAssignment(assignment) {
        this.syncingAssignment = true;
        this.syncingAssignmentId = assignment.id;
        try {
          const { data } = await axios.get(`/api/admin/assignments/${assignment.id}/sync`);
          this.getAssignmentsPaged();
          this.$toast.open({ message: 'Sync complete!', color: 'green-500', duration: 6000 });
          this.syncingAssignment = false;
          this.syncingAssignmentId = 0;
        } catch (error) {
          this.$toast.open({ message: error.message, color: 'red-500', duration: 4000 });
          this.syncingAssignment = false;
          this.syncingAssignmentId = 0;
        }
      },

      launchAssignmentsModal(assignment) {
        this.$modal.open({
          parent: this,
          component: AssignmentTemplateModal,
          hasModalCard: true,
          width: 800,
          canCancel: ['button', 'x'],
          props: {
            assignment,
            duplicate: false,
          },
          events: {
            getAssignmentsPaged: () => this.getAssignmentsPaged(),
          },
        });
      },

      launchDuplicateAssignmentModal(assignment) {
        this.$modal.open({
          parent: this,
          component: AssignmentTemplateModal,
          hasModalCard: true,
          width: 800,
          canCancel: ['button', 'x'],
          props: {
            assignment,
            duplicate: true,
          },
          events: {
            getAssignmentsPaged: () => this.getAssignmentsPaged(),
          },
        });
      },

      launchIndividualAssigneesSideDrawer(assignmentTemplate) {
        this.$sideDrawer.open({
          parent: this,
          component: IndividualAssigneeSideDrawer,
          props: {
            assignmentTemplateId: assignmentTemplate.id,
            userId: assignmentTemplate.userId,
          },
        });
      },

      launchGroupAssigneesSideDrawer(assignmentTemplate) {
        this.$sideDrawer.open({
          parent: this,
          component: GroupAssigneesSideDrawer,
          props: {
            groupName: assignmentTemplate.groupName,
            assignmentTemplateId: assignmentTemplate.id,
          },
        });
      },

      async deleteAssignment(assignment) {
        this.$dialog.confirm({
          title: 'Deleting Assignment',
          message: `Are you sure you want to <b>delete</b> ${assignment.name}? <br><br><b>This will delete all related User Assignments and User Tasks!</b></br></br>This action cannot be undone.`,
          confirmText: 'Delete Assignment',
          confirmColor: 'red-500',
          onConfirm: async () => {
            await axios.delete(`/api/admin/assignments/${assignment.id}`);
            this.getAssignmentsPaged();
            this.$toast.open('Assignment deleted!');
          },
        });
      },

      async archiveAssignment(assignment) {
        this.$dialog.confirm({
          title: 'Archiving Assignment',
          message: `Are you sure you want to <b>archive</b> ${assignment.name}? <br><br><b>You should only do this if the assignment is fully completed. Archiving the assignment will hide it from this list by default!</b></br></br>This action cannot be undone.`,
          confirmText: 'Archive Assignment',
          confirmColor: 'red-500',
          onConfirm: async () => {
            await axios.post(`/api/admin/assignments/${assignment.id}/archive`);
            this.getAssignmentsPaged();
            this.$toast.open('Assignment archived!');
          },
        });
      },

      onSort(field, order) {
        this.pagination.sortField = field;
        this.pagination.sortOrder = order;
      },

    },
  };
</script>